import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { addScene, removeScene, switchScene, MAX_SCENES } from "../../store/sceneSlice";
import { DEFAULT_STAGE_GRID_HEIGHT, DEFAULT_STAGE_GRID_WIDTH, STAGE_CELL_SIZE } from "../../constants/stageDimensions";
import "../../css/SceneManager.css";

const SCALE = 0.25; // shrink to 25% for preview

export default function SceneManager() {
  const dispatch = useDispatch();
  const {
    scenes,
    currentSceneIndex,
    drawLineSettings,
    stageGridWidth,
    stageGridHeight
  } = useSelector((state) => state.scene);
  const limitReached = scenes.length >= MAX_SCENES;
  const basePath = "./assets/";

  const gridWidth = Math.max(1, stageGridWidth || DEFAULT_STAGE_GRID_WIDTH);
  const gridHeight = Math.max(1, stageGridHeight || DEFAULT_STAGE_GRID_HEIGHT);
  const cellWidthPercent = (1 / gridWidth) * 100;
  const cellHeightPercent = (1 / gridHeight) * 100;
  const obstacleWidthPercent = (2 / gridWidth) * 100;
  const obstacleHeightPercent = (2 / gridHeight) * 100;
  const obstacleSquarePercent = Math.min(obstacleWidthPercent, obstacleHeightPercent);

  // Obstacle renderer for preview
  const renderObstaclePreview = (obstacle) => {
    const sizePercent = obstacleSquarePercent;
    let obstacleElement;
    switch (obstacle.shape) {
      case 'square':
        obstacleElement = (
          <div
            style={{
              width: `${sizePercent}%`,
              height: `${sizePercent}%`,
              backgroundColor: '#4a90e2',
              borderRadius: '2px',
            }}
          />
        );
        break;
      case 'triangle':
        obstacleElement = (
          <div
            style={{
              width: `${sizePercent}%`,
              height: `${sizePercent}%`,
              backgroundColor: '#28a745',
              clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
            }}
          />
        );
        break;
      case 'circle':
        obstacleElement = (
          <div
            style={{
              width: `${sizePercent}%`,
              height: `${sizePercent}%`,
              backgroundColor: '#ffc107',
              borderRadius: '50%',
            }}
          />
        );
        break;
      default:
        obstacleElement = (
          <div
            style={{
              width: `${obstacleWidthPercent}%`,
              height: `${obstacleHeightPercent}%`,
              backgroundColor: '#6c757d',
              borderRadius: '2px',
            }}
          />
        );
    }

    return (
      <div
        key={obstacle.id}
        className="scene-thumb-obstacle"
        style={{
          position: 'absolute',
          left: `${((obstacle.x + 0.5) / gridWidth) * 100}%`,
          top: `${((obstacle.y + 0.5) / gridHeight) * 100}%`,
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
        }}
      >
        {obstacleElement}
      </div>
    );
  };

  const renderTrailOverlay = (scene) => {
    if (!scene || !drawLineSettings?.enabled) return null;
    const trails = scene.actorTrails;
    if (!trails) return null;
    const entries = Object.entries(trails).filter(
      ([, points]) => Array.isArray(points) && points.length > 1
    );
    if (!entries.length) return null;

    const strokeWidth = Math.max(0.5, (drawLineSettings.thickness || 3) * SCALE * (DEFAULT_STAGE_GRID_WIDTH / gridWidth));

    return (
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${gridWidth} ${gridHeight}`}
        preserveAspectRatio="none"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          pointerEvents: 'none',
          zIndex: 1,
        }}
      >
        {entries.map(([actorId, points]) => {
          let d = '';
          points.forEach((point, idx) => {
            const px = point.x + 0.5;
            const py = point.y + 0.5;
            d += `${idx === 0 ? 'M' : ' L'} ${px} ${py}`;
          });
          return (
            <path
              key={`scene-${scene.id}-trail-${actorId}`}
              d={d}
              stroke={drawLineSettings.color || '#FF0000'}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
              opacity={0.85}
            />
          );
        })}
      </svg>
    );
  };

  return (
    <div className="scene-manager-xscroll">
      {scenes.map((scene, idx) => {
        const background = scene.background || "#ffffff";
        const bgStyle = background.startsWith("#")
          ? { backgroundColor: background }
          : {
              backgroundImage: `url(${background})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat"
            };

        return (
          <div
            key={scene.id}
            className={`scene-thumb-h ${idx === currentSceneIndex ? "selected" : ""}`}
            onClick={() => dispatch(switchScene(idx))}
            title={`Page ${idx + 1}`}
          >
            <div className="scene-thumb-preview">
              <div
                className="scene-thumb-canvas"
                style={{
                  ...bgStyle,
                  position: "relative",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  overflow: "hidden",
                  boxSizing: "border-box"
                }}
              >
                {/* Render obstacles in preview */}
                {scene.obstacles?.map(renderObstaclePreview)}

                {renderTrailOverlay(scene)}

                {/* Render actors in preview */}
                {scene.actors?.map(actor => {
                  if (actor.visible === false) return null;
                  const actorWidthPercent = (4 * (actor.size || 1) / gridWidth) * 100;
                  const actorHeightPercent = (4 * (actor.size || 1) / gridHeight) * 100;
                  return(
                    <img
                    className="scene-thumb-character"
                    key={actor.id}
                    src={actor.image || basePath + "characters/stembot.png"}
                    alt={actor.name}
                    draggable={false}
                    style={{
                      position: "absolute",
                      width: `${actorWidthPercent}%`,
                      height: `${actorHeightPercent}%`,
                      left: `${((actor.x + 0.5) / gridWidth) * 100}%`,
                      top: `${((actor.y + 0.5) / gridHeight) * 100}%`,
                      transform: `translate(-50%, -50%) rotate(${actor.direction || 0}deg)`,
                      pointerEvents: "none",
                    }}
                  />
                  )
             })}
              </div>
            </div>

            <span className="scene-badge">{idx + 1}</span>

            {scenes.length > 1 && (
              <button
                className="scene-remove-btn"
                onClick={e => {
                  e.stopPropagation();
                  dispatch(removeScene(idx));
                }}
                title="Delete page"
                tabIndex={-1}
                aria-label={`Delete page ${idx + 1}`}
              >
                ×
              </button>
            )}
          </div>
        );
      })}
      <div
        className={`scene-plus-h ${limitReached ? "disabled" : ""}`}
        onClick={() => {
          if (!limitReached) dispatch(addScene());
        }}
        title={limitReached ? `Limit ${MAX_SCENES} pages reached` : "Add page"}
        aria-disabled={limitReached}
      >
        <img src={basePath + "ui/newpage.png"} alt="Add New Scene" draggable={false} />
        {limitReached && (
          <span style={{ fontSize: 11, color: "#888", marginTop: 4, display: "block" }}>
            Max {MAX_SCENES}
          </span>
        )}
      </div>
    </div>
  );
}
